// src/components/onboarding/PropertySetupForm.tsx
//
// Renders the Property Setup guide as a schema-driven form. The schema
// (propertySetupSchema.json) lists the exact docx template anchors grouped by
// section; this component renders one control per anchor — checkboxes as toggles,
// text anchors as inputs — and reports every change through the flat `anchors` map.
// Purely controlled: no internal data state, keyed by the docx anchor `key`.

import React from 'react';
import {
    PROPERTY_SETUP_SCHEMA_SECTIONS,
    SECTION_INTROS,
    FIELD_HINTS,
    deriveLabel,
} from '../../config/propertySetupSchemaConfig';
import { PropertySetupAnchors } from '../../types';

interface PropertySetupFormProps {
    value: PropertySetupAnchors;
    onChange: (next: PropertySetupAnchors) => void;
}

const PropertySetupForm: React.FC<PropertySetupFormProps> = ({ value, onChange }) => {
    const updateAnchor = (key: string, newValue: string | boolean) => {
        onChange({ ...value, [key]: newValue });
    };

    return (
        <div className="space-y-8">
            {PROPERTY_SETUP_SCHEMA_SECTIONS.map((section) => {
                const intro = SECTION_INTROS[section.section];
                return (
                    <section
                        key={section.section}
                        className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8"
                    >
                        <h3 className="font-serif font-bold text-xl text-primary mb-1">{section.section}</h3>
                        {intro && <p className="text-sm text-gray-500 mb-5 leading-relaxed">{intro}</p>}
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                            {section.entries.map((entry) => {
                                const label = deriveLabel(entry);
                                const hint = FIELD_HINTS[entry.key];
                                if (entry.type === 'checkbox') {
                                    return (
                                        <label
                                            key={entry.key}
                                            className="flex items-start gap-3 cursor-pointer group py-1 md:col-span-2"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={Boolean(value[entry.key])}
                                                onChange={(e) => updateAnchor(entry.key, e.target.checked)}
                                                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-brand focus:ring-brand/30 accent-brand shrink-0"
                                            />
                                            <span className="text-sm text-gray-700 leading-snug group-hover:text-gray-900">
                                                {label}
                                                {hint && <span className="block text-xs text-gray-400 mt-0.5">{hint}</span>}
                                            </span>
                                        </label>
                                    );
                                }
                                const raw = value[entry.key];
                                return (
                                    <div key={entry.key}>
                                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                                            {label}
                                        </label>
                                        <input
                                            type="text"
                                            value={typeof raw === 'string' ? raw : ''}
                                            onChange={(e) => updateAnchor(entry.key, e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                        />
                                        {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default PropertySetupForm;
